import { ArrowDown, Check, X } from 'lucide-react';
import goldMark from '../../assets/Gold colour Logo (1)- copy.png';
import type { License } from '../../types/domain';

const GRANTED = [
  'Authorization to promote and resell Lyrixsa AI under the applicable Reseller Terms',
  '70% commission across the whole funnel on qualifying sales, credited through the applicable platform',
  'Right of use to Lyrixsa AI sales pages, sales copy, swipes and marketing graphics',
  'Permission to run paid advertising to those materials',
  'Custom marketing graphics on request through support',
  'Lyrixsa AI commercial licensing for customers who purchase through the holder, for the songs they create',
];

const NOT_GRANTED = [
  'Ownership of Lyrixsa AI, its software, source code, trademarks, branding or intellectual property',
  'White-label, rebranding, source-code access or the right to modify the software',
  'Authority to set or change Lyrixsa AI pricing',
  'Unlimited sublicensing, transfer or assignment of the software',
  'Exclusivity, exclusive territory, or a permanently fixed commission rate',
  'Lyrixsa Unlimited or the Lyrixsa AI Bundle, which are separate products on their own terms',
  'Any guarantee of sales, customers, commissions, earnings or results',
];

function statusLabel(status: License['status']): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function Row({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="lyx-reseller-cert-row">
      <span className="lyx-reseller-cert-row-label">{label}</span>
      <span
        className={mono ? 'lyx-reseller-cert-row-value is-mono' : 'lyx-reseller-cert-row-value'}
      >
        {children}
      </span>
    </div>
  );
}

export default function ResellerCertificate({
  license,
  onDownload,
  downloading = false,
}: {
  license: License;
  onDownload: () => void;
  downloading?: boolean;
}) {
  const series = license.licenseNumber.split('-').slice(0, 2).join('-');
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&color=000000&bgcolor=ffffff&data=${encodeURIComponent(license.verifyUrl)}`;

  return (
    <article className="lyx-reseller-cert" aria-label="Lyrixsa AI Reseller License certificate">
      <button
        type="button"
        className="lyx-reseller-cert-download"
        onClick={onDownload}
        disabled={downloading}
        aria-label={downloading ? 'Downloading reseller certificate' : 'Download reseller certificate'}
        title="Download reseller certificate"
      >
        <ArrowDown aria-hidden="true" />
      </button>
      <div className="lyx-reseller-cert-inner">
        <header className="lyx-reseller-cert-head">
          <img src={goldMark} alt="" aria-hidden="true" />
          <span className="lyx-reseller-cert-brand">Lyrixsa AI</span>
          <span className="lyx-reseller-cert-series">
            Reseller series
            <br />
            {series}
          </span>
        </header>

        <div className="lyx-reseller-cert-title">
          <h2>Reseller License Certificate</h2>
          <p>Certificate of Reseller Authorization</p>
          <i aria-hidden="true" />
        </div>

        <p className="lyx-reseller-cert-attest">
          This certifies that the holder named below is an{' '}
          <strong>authorized Lyrixsa AI reseller</strong>, permitted to promote and resell Lyrixsa
          AI in accordance with the applicable Lyrixsa AI Reseller Terms, and to be credited
          commission on qualifying sales under those terms.
        </p>

        <div className="lyx-reseller-cert-rows">
          <Row label="License holder">{license.customerName}</Row>
          <Row label="Account email">{license.email}</Row>
          <Row label="Reseller license number" mono>
            {license.licenseNumber}
          </Row>
          <Row label="Commission rate">
            <b>70%</b> across the funnel, on qualifying sales
          </Row>
          <Row label="Status">{statusLabel(license.status)}</Row>
          <Row label="Issued on">{license.issuedDate}</Row>
          <Row label="Issued by">Geecore Limited, owner of Lyrixsa AI</Row>
        </div>

        <div className="lyx-reseller-cert-grants">
          <section className="is-granted">
            <h2>This license grants</h2>
            <ul>
              {GRANTED.map((item) => (
                <li key={item}>
                  <Check aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
          <section className="is-not-granted">
            <h2>This license does not grant</h2>
            <ul>
              {NOT_GRANTED.map((item) => (
                <li key={item}>
                  <X aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <footer className="lyx-reseller-cert-foot">
          <div className="lyx-reseller-cert-signature">
            <span>Geecore Limited</span>
            <b>Issuing authority · Owner of Lyrixsa AI</b>
            <p>
              This certificate evidences reseller authorization only. The Lyrixsa AI Reseller Terms
              and Terms of Service govern and prevail over any summary shown here.
            </p>
          </div>
          <a
            href={license.verifyUrl}
            target="_blank"
            rel="noreferrer"
            className="lyx-reseller-cert-qr"
          >
            <img src={qrSrc} alt={`Verify reseller license ${license.licenseNumber}`} />
            <span>Scan to verify</span>
          </a>
        </footer>
      </div>
    </article>
  );
}
